// apps/web/src/components/SignInSheet.tsx
// Bottom sheet for EXISTING users to sign in from the Get Started screen.
// Apple/Google (the paths already used in Login), plus the required privacy
// line. Success → the app. New users don't come here — they tap Get Started.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT, apiLang, useLocale } from "../lib/i18n";
import { SheetShell } from "./SheetShell";
import { SocialButton } from "./SocialAuthButtons";
import { EmailCodeAuth } from "./EmailCodeAuth";
import { isNativeApp } from "../lib/native";
import { nativeAppleSignIn } from "../lib/nativeApple";
import { nativeBrowserOAuth } from "../lib/nativeOAuth";

type Provider = "apple" | "google";

interface Props {
  open: boolean;
  onClose: () => void;
  onAuthed: () => void;
}

export function SignInSheet({ open, onClose, onAuthed }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const native = isNativeApp();
  const [providers, setProviders] = useState<Provider[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showEmail, setShowEmail] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErr(null); setShowEmail(false);
    let cancelled = false;
    api.authProviders()
      .then((r) => { if (!cancelled) setProviders(r.providers); })
      .catch(() => { if (!cancelled) setProviders([]); });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  async function appleNative() {
    setErr(null);
    try { await nativeAppleSignIn(apiLang(locale)); onAuthed(); }
    catch { setErr(t("auth.oauthError")); }
  }
  async function googleNative() {
    setErr(null);
    try { await nativeBrowserOAuth("google"); onAuthed(); }
    catch (e) { if (!(e instanceof Error && e.message === "cancelled")) setErr(t("auth.oauthError")); }
  }

  return (
    <SheetShell onClose={onClose}>
      <h3 className="text-lg font-semibold mt-2 mb-4">{t("auth.loginTitle")}</h3>

      {err && <p className="text-sm text-danger mb-3">{err}</p>}

      {providers !== null && providers.length > 0 && (
        <div className="space-y-2">
          {providers.includes("apple") && (native
            ? <SocialButton provider="apple" onClick={appleNative}>{t("auth.continueWithApple")}</SocialButton>
            : <SocialButton provider="apple">{t("auth.continueWithApple")}</SocialButton>)}
          {providers.includes("google") && (native
            ? <SocialButton provider="google" onClick={googleNative}>{t("auth.continueWithGoogle")}</SocialButton>
            : <SocialButton provider="google">{t("auth.continueWithGoogle")}</SocialButton>)}
        </div>
      )}

      {/* Email-code fallback (also serves dev where no OAuth is configured). */}
      {providers !== null && (providers.length === 0 || showEmail) && (
        <div className={providers.length > 0 ? "mt-5" : ""}>
          <EmailCodeAuth onAuthed={onAuthed} />
        </div>
      )}
      {providers !== null && providers.length > 0 && !showEmail && (
        <button onClick={() => setShowEmail(true)} className="mt-4 w-full text-center text-sm text-fg-muted press">
          {t("auth.loginWithEmail")}
        </button>
      )}

      <p className="mt-5 text-center text-sm leading-relaxed text-fg-faint">
        {t("start.privacy_pre")}{" "}
        <Link to="/privacy" className="underline">{t("start.privacy_link")}</Link>
      </p>
    </SheetShell>
  );
}
